/*
 * Mappers from admin-availability grid data → Calendar events/layer intervals.
 * Used by admin/availability (Team Schedule) to render a single provider's
 * schedule in the unified <Calendar>.
 */

import { parseISO } from 'date-fns';
import type {
  ProviderDayData,
  ProviderBookingBlock,
  ProviderAvailabilityBlock,
  ProviderOverrideBlock,
  ProviderCalendarBlock,
  ProviderGroupClassSlotBlock,
} from '@/services/api/admin-availability.service';
import type {
  CalendarEvent,
  CalendarLayerInterval,
} from './types';
import { buildBookingEvent } from './bookings';
import { humanBusyReason } from './slots';

function combine(dateStr: string, time: string): Date {
  const [h, m] = time.split(':');
  const d = parseISO(`${dateStr}T00:00:00`);
  d.setHours(parseInt(h, 10), parseInt(m, 10), 0, 0);
  return d;
}

export interface AdminCalendarBundle {
  events: CalendarEvent[];
  availability: CalendarLayerInterval[];
  overrideAvailable: CalendarLayerInterval[];
  overrideBlocked: CalendarLayerInterval[];
  groupClass: CalendarLayerInterval[];
  busy: CalendarLayerInterval[];
}

export interface AdminCalendarBundleOptions {
  /** Label shown on busy intervals when the backend reason is technical (e.g. "calendar_sync"). */
  busyFallbackLabel?: string;
  /**
   * Label for availability windows with no services linked. The backend
   * treats those as open to every service, so say so instead of an empty block.
   */
  allServicesLabel?: string;
}

export function buildAdminCalendarBundle(
  days: Record<string, ProviderDayData>,
  opts: AdminCalendarBundleOptions = {},
): AdminCalendarBundle {
  const events: CalendarEvent[] = [];
  const availability: CalendarLayerInterval[] = [];
  const overrideAvailable: CalendarLayerInterval[] = [];
  const overrideBlocked: CalendarLayerInterval[] = [];
  const groupClass: CalendarLayerInterval[] = [];
  const busy: CalendarLayerInterval[] = [];

  for (const [dateStr, day] of Object.entries(days)) {
    for (const b of day.bookings ?? []) {
      events.push(bookingBlockToEvent(b, dateStr));
    }
    for (const a of day.availabilities ?? []) {
      availability.push(availabilityBlockToInterval(a, dateStr, opts.allServicesLabel));
    }
    for (const o of day.overrides ?? []) {
      const interval = overrideBlockToInterval(o, dateStr);
      const isAvail = o.type === 'extra' || o.type === 'modified';
      (isAvail ? overrideAvailable : overrideBlocked).push(interval);
    }
    for (const c of day.calendar_blocks ?? []) {
      busy.push(calendarBlockToInterval(c, dateStr, opts.busyFallbackLabel));
    }
    for (const g of day.group_class_slots ?? []) {
      groupClass.push(groupClassToInterval(g, dateStr));
    }
  }

  return { events, availability, overrideAvailable, overrideBlocked, groupClass, busy };
}

function bookingBlockToEvent(b: ProviderBookingBlock, dateStr: string): CalendarEvent {
  return buildBookingEvent({
    id: b.id,
    start: combine(dateStr, b.start_time),
    end: combine(dateStr, b.end_time),
    customerName: b.customer_name,
    serviceName: b.service_name,
    status: b.status,
    columnId: dateStr,
    meta: { kind: 'booking', booking: b, dateStr },
  });
}

function availabilityBlockToInterval(
  a: ProviderAvailabilityBlock,
  dateStr: string,
  allServicesLabel?: string,
): CalendarLayerInterval {
  const svcNames = a.services?.map((s) => s.name).filter(Boolean) ?? [];
  const label = svcNames.length === 0 ? allServicesLabel : svcNames.join(', ');
  return {
    id: `avail-${dateStr}-${a.id}`,
    start: combine(dateStr, a.start_time),
    end: combine(dateStr, a.end_time),
    columnId: dateStr,
    label,
    data: { kind: 'availability', availability: a, dateStr },
  };
}

function overrideBlockToInterval(
  o: ProviderOverrideBlock,
  dateStr: string,
): CalendarLayerInterval {
  const svcNames = o.services?.map((s) => s.name).filter(Boolean) ?? [];
  const svcLabel = svcNames.length > 0 ? svcNames.join(', ') : undefined;
  const label = svcLabel ?? o.reason ?? undefined;
  if (!o.start_time || !o.end_time) {
    return {
      id: `ovr-${dateStr}-${o.id}`,
      start: combine(dateStr, '00:00'),
      end: combine(dateStr, '23:59'),
      columnId: dateStr,
      label,
      data: { kind: 'override', override: o, dateStr },
    };
  }
  return {
    id: `ovr-${dateStr}-${o.id}`,
    start: combine(dateStr, o.start_time),
    end: combine(dateStr, o.end_time),
    columnId: dateStr,
    label,
    data: { kind: 'override', override: o, dateStr },
  };
}

function calendarBlockToInterval(
  c: ProviderCalendarBlock,
  dateStr: string,
  fallbackLabel?: string,
): CalendarLayerInterval {
  return {
    id: `busy-${dateStr}-${c.id}`,
    start: combine(dateStr, c.start_time),
    end: combine(dateStr, c.end_time),
    columnId: dateStr,
    label: humanBusyReason(c.reason, fallbackLabel),
    data: { kind: 'busy', block: c, dateStr },
  };
}

function groupClassToInterval(
  g: ProviderGroupClassSlotBlock,
  dateStr: string,
): CalendarLayerInterval {
  return {
    id: `gc-${dateStr}-${g.id}`,
    start: combine(dateStr, g.start_time),
    end: combine(dateStr, g.end_time),
    columnId: dateStr,
    label: `${g.service.name} · ${g.current_bookings}/${g.max_capacity}`,
    data: { kind: 'group-class', slot: g, dateStr },
  };
}
